import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, User, Mail, Phone, Shield, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import type { CreateUserFormData, CreateUserFormErrors, AdminUser, AdminUserRole } from '../../types';
import { trialDurationLabel } from '@/hooks/usePlanStatus';

interface TrialOffer {
  key: string;
  code: string;
  days: number;
  message_limit: number;
  active: boolean;
}

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUserCreated: (user: AdminUser) => void;
}

const initialFormData: CreateUserFormData = {
  email: '',
  first_name: '',
  last_name: '',
  phone: '',
  role: 'operator',
  company_data: null,
  trial_offer_key: '',
};

export const CreateUserModal: React.FC<CreateUserModalProps> = ({ isOpen, onClose, onUserCreated }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CreateUserFormData>(initialFormData);
  const [errors, setErrors] = useState<CreateUserFormErrors>({});
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [trialOffers, setTrialOffers] = useState<TrialOffer[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    axios
      .get<TrialOffer[]>('/admin/trial-offers')
      .then((res) => setTrialOffers(res.data || []))
      .catch(() => setTrialOffers([]));
  }, [isOpen]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string) => {
    const phoneRegex = /^\+?[\d\s\-\(\)]{8,}$/;
    return phoneRegex.test(phone);
  };

  const handleInputChange = (field: keyof CreateUserFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
    if (field === 'email') setIsValidEmail(validateEmail(value));
  };

  const validateForm = () => {
    const newErrors: CreateUserFormErrors = {};
    if (!formData.email.trim()) newErrors.email = 'Email es requerido';
    else if (!validateEmail(formData.email)) newErrors.email = 'Email no válido';
    if (!formData.first_name.trim()) newErrors.first_name = 'Nombre es requerido';
    if (!formData.last_name.trim()) newErrors.last_name = 'Apellido es requerido';
    if (!formData.phone.trim()) newErrors.phone = 'Teléfono es requerido';
    else if (!validatePhone(formData.phone)) newErrors.phone = 'Formato de teléfono inválido';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Por favor corrige los errores en el formulario');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...formData,
        trial_offer_key: formData.trial_offer_key || undefined,
      };
      const response = await axios.post<AdminUser>('/admin/users', payload);
      toast.success('Usuario creado exitosamente', {
        description: `${response.data.first_name} ${response.data.last_name} (${response.data.role})`
      });
      setFormData(initialFormData);
      setErrors({});
      setIsValidEmail(false);
      onUserCreated(response.data);
      onClose();
    } catch (error: any) {
      console.error('Error creating user:', error);
      if (error.response?.data?.detail?.includes('already exists')) {
        toast.error('Usuario ya existe', { description: 'Este email ya está registrado' });
        setErrors({ email: 'Este email ya está registrado' });
      } else {
        toast.error('Error al crear usuario', {
          description: error.response?.data?.detail || 'Error desconocido'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData(initialFormData);
      setErrors({});
      setIsValidEmail(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 text-white flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <User className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Crear Usuario</h2>
              <p className="text-blue-100 text-sm">Agregar nuevo usuario al sistema</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {/* Content */}
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => handleInputChange('email', e.target.value)}
                  placeholder="usuario@empresa.com"
                  className={`w-full pl-10 pr-10 py-2.5 border-2 rounded-lg focus:outline-none transition-all text-sm ${
                    errors.email
                      ? 'border-red-300 bg-red-50'
                      : formData.email && isValidEmail
                      ? 'border-green-300 bg-green-50'
                      : formData.email && !isValidEmail
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-200 focus:border-blue-500 focus:bg-blue-50'
                  }`}
                  disabled={loading}
                />
                {formData.email && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    {isValidEmail ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-red-500" />
                    )}
                  </div>
                )}
              </div>
              {errors.email && (
                <p className="text-red-600 text-xs mt-1">{errors.email}</p>
              )}
            </div>
            {/* Name fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nombre *</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={e => handleInputChange('first_name', e.target.value)}
                  placeholder="Carlos"
                  className={`w-full px-3 py-2.5 border-2 rounded-lg focus:outline-none transition-all text-sm ${
                    errors.first_name
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-200 focus:border-blue-500 focus:bg-blue-50'
                  }`}
                  disabled={loading}
                />
                {errors.first_name && (
                  <p className="text-red-600 text-xs mt-1">{errors.first_name}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Apellido *</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={e => handleInputChange('last_name', e.target.value)}
                  placeholder="Martínez"
                  className={`w-full px-3 py-2.5 border-2 rounded-lg focus:outline-none transition-all text-sm ${
                    errors.last_name
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-200 focus:border-blue-500 focus:bg-blue-50'
                  }`}
                  disabled={loading}
                />
                {errors.last_name && (
                  <p className="text-red-600 text-xs mt-1">{errors.last_name}</p>
                )}
              </div>
            </div>
            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono *</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={e => handleInputChange('phone', e.target.value)}
                  placeholder="+34 612 345 678"
                  className={`w-full pl-10 pr-3 py-2.5 border-2 rounded-lg focus:outline-none transition-all text-sm ${
                    errors.phone
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-200 focus:border-blue-500 focus:bg-blue-50'
                  }`}
                  disabled={loading}
                />
              </div>
              {errors.phone && (
                <p className="text-red-600 text-xs mt-1">{errors.phone}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">Incluye código de país (ej: +34)</p>
            </div>
            {/* Role selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de cuenta *</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Shield className="h-4 w-4 text-gray-400" />
                </div>
                <select
                  value={formData.role}
                  onChange={e => handleInputChange('role', e.target.value as AdminUserRole)}
                  className="w-full pl-10 pr-3 py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 focus:bg-blue-50 transition-all text-sm"
                  disabled={loading}
                >
                  <option value="operator">👤 Operador - Gestionar campañas</option>
                  <option value="admin">👑 Administrador - Acceso completo</option>
                </select>
              </div>
              <p className="text-xs text-gray-500 mt-1">El usuario completará su onboarding al iniciar sesión</p>
            </div>
            {/* Trial duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Duración de prueba gratis</label>
              <select
                value={formData.trial_offer_key || ''}
                onChange={e => handleInputChange('trial_offer_key', e.target.value)}
                className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 focus:bg-blue-50 transition-all text-sm"
                disabled={loading}
              >
                <option value="">Default del sistema</option>
                {trialOffers.map((offer) => (
                  <option key={offer.key} value={offer.key}>
                    {trialDurationLabel(offer.days)} · {offer.message_limit} msgs ({offer.code})
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Define la secuencia de mensajes (7 / 15 / 30 días)</p>
            </div>
          </form>
        </div>
        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors text-sm"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isValidEmail || !formData.first_name || !formData.last_name || !formData.phone || loading}
            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm flex items-center space-x-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Creando...</span>
              </>
            ) : (
              <>
                <User className="h-4 w-4" />
                <span>Crear Usuario</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateUserModal;
